import type { Editor } from '@tiptap/vue-3'
import type { Ref } from 'vue'

export interface RichEditorToolbarAction {
  disabled?: boolean
  icon: string
  isActive: boolean
  // ключ i18n для aria-подписи кнопки, резолвится через $t в шаблоне
  label: string
  run: () => void
}

export type RichEditorHeadingLevel = 1 | 2

export type RichEditorTextAlign = 'left' | 'center' | 'right' | 'justify'

export const RICH_EDITOR_FONT_SIZES = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '30px',
  '32px',
  '34px',
  '36px',
  '38px',
  '40px',
] as const

export type RichEditorFontSize = (typeof RICH_EDITOR_FONT_SIZES)[number]

const DEFAULT_TEXT_COLOR = '#000000'

// Разрешённые схемы ссылок: остальное (javascript:, data: и т.п.) блокируем как XSS-вектор
const SAFE_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:']

// Ссылки без схемы считаем относительными и безопасными; ссылки со схемой пропускаем только из allowlist
export const isSafeLinkUrl = (url: string): boolean => {
  const trimmed = url.trim()
  if (trimmed === '') {
    return false
  }

  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return true
  }

  try {
    return SAFE_LINK_PROTOCOLS.includes(new URL(trimmed).protocol)
  } catch {
    return false
  }
}

export const useRichEditorToolbar = (editor: Ref<Editor | undefined>) => {
  const textColor = computed(() => editor.value?.getAttributes('textStyle').color ?? DEFAULT_TEXT_COLOR)

  const toggleHeading = (level: RichEditorHeadingLevel) => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return
    }

    editorInstance.chain().focus().resetAttributes('textStyle', ['fontSize']).setHeading({ level }).run()
  }

  // null — когда у текста нет явного размера (используется размер по умолчанию), чтобы не показывать ложный «10px»
  const currentFontSize = computed<RichEditorFontSize | null>(() => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return null
    }

    for (const size of RICH_EDITOR_FONT_SIZES) {
      if (editorInstance.isActive('textStyle', { fontSize: size })) {
        return size
      }
    }

    return null
  })

  const setFontSize = (size: string) => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return
    }

    editorInstance.chain().focus().clearNodes().setFontSize(size).run()
  }

  const setColor = (value: string) => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return
    }

    editorInstance.chain().focus().setColor(value).run()
  }

  const setLink = () => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return
    }

    const previousUrl = editorInstance.getAttributes('link').href
    // eslint-disable-next-line no-alert
    const url = window.prompt('URL', previousUrl)

    if (url === null) {
      return
    }

    if (url === '') {
      editorInstance.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }

    // Отсекаем небезопасные схемы (javascript:, data: и пр.) до записи в документ
    if (!isSafeLinkUrl(url)) {
      return
    }

    editorInstance.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  const setTextAlign = (align: RichEditorTextAlign) => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return
    }

    editorInstance.chain().focus().setTextAlign(align).run()
  }

  const handleUndo = () => {
    const editorInstance = editor.value
    if (!editorInstance?.can().undo()) {
      return
    }

    editorInstance.chain().focus().undo().run()
  }

  const handleRedo = () => {
    const editorInstance = editor.value
    if (!editorInstance?.can().redo()) {
      return
    }

    editorInstance.chain().focus().redo().run()
  }

  const formatActions = computed<RichEditorToolbarAction[]>(() => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return []
    }

    return [
      {
        icon: 'fa-bold',
        label: 'editor_toolbar_bold',
        isActive: editorInstance.isActive('bold'),
        run: () => {
          editorInstance.chain().focus().toggleBold().run()
        },
      },
      {
        icon: 'fa-italic',
        label: 'editor_toolbar_italic',
        isActive: editorInstance.isActive('italic'),
        run: () => {
          editorInstance.chain().focus().toggleItalic().run()
        },
      },
      {
        icon: 'fa-underline',
        label: 'editor_toolbar_underline',
        isActive: editorInstance.isActive('underline'),
        run: () => {
          editorInstance.chain().focus().toggleUnderline().run()
        },
      },
      {
        icon: 'fa-link',
        label: 'editor_toolbar_link',
        isActive: editorInstance.isActive('link'),
        run: setLink,
      },
    ]
  })

  const alignActions = computed<RichEditorToolbarAction[]>(() => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return []
    }

    const alignments: Array<{ align: RichEditorTextAlign; icon: string; label: string }> = [
      { align: 'left', icon: 'fa-align-left', label: 'editor_toolbar_align_left' },
      { align: 'center', icon: 'fa-align-center', label: 'editor_toolbar_align_center' },
      { align: 'right', icon: 'fa-align-right', label: 'editor_toolbar_align_right' },
      { align: 'justify', icon: 'fa-align-justify', label: 'editor_toolbar_align_justify' },
    ]

    return alignments.map(({ align, icon, label }) => ({
      icon,
      label,
      isActive: editorInstance.isActive({ textAlign: align }),
      run: () => {
        setTextAlign(align)
      },
    }))
  })

  const historyActions = computed<RichEditorToolbarAction[]>(() => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return []
    }

    return [
      {
        icon: 'fa-rotate-left',
        label: 'editor_toolbar_undo',
        isActive: false,
        disabled: !editorInstance.can().undo(),
        run: handleUndo,
      },
      {
        icon: 'fa-rotate-right',
        label: 'editor_toolbar_redo',
        isActive: false,
        disabled: !editorInstance.can().redo(),
        run: handleRedo,
      },
    ]
  })

  const fontSizeOptions = RICH_EDITOR_FONT_SIZES.map((size) => ({
    label: size,
    value: size,
  }))

  const headingOptions = [
    { label: 'H1', key: '1' },
    { label: 'H2', key: '2' },
  ]

  const handleHeadingSelect = (key: string | number) => {
    if (key === '1' || key === '2') {
      toggleHeading(Number(key) as RichEditorHeadingLevel)
    }
  }

  const isHeadingActive = computed(() => {
    const editorInstance = editor.value
    if (!editorInstance) {
      return false
    }

    return editorInstance.isActive('heading', { level: 1 }) || editorInstance.isActive('heading', { level: 2 })
  })

  const isFontSizeActive = computed(() => currentFontSize.value !== null)

  const isTextColorActive = computed(() => textColor.value !== DEFAULT_TEXT_COLOR)

  return {
    alignActions,
    currentFontSize,
    fontSizeOptions,
    formatActions,
    handleHeadingSelect,
    headingOptions,
    historyActions,
    isFontSizeActive,
    isHeadingActive,
    isTextColorActive,
    setColor,
    setFontSize,
    textColor,
    toggleHeading,
  }
}
